import {
  Center,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  type UseModalProps,
  useDisclosure,
} from "@chakra-ui/react";
import { Plural, Trans, t } from "@lingui/macro";
import {
  type ModalInvoicesOurWorkBiQuery,
  useModalInvoicesExpenseBiQuery,
  useModalInvoicesOurWorkBiQuery,
  useModalInvoicesProjectQuery,
} from "@src/__generated__/graphql";
import { onError } from "@src/utils/apollo";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import React, { type FC, useMemo, useState } from "react";
import { ModalGetCredit } from "../ModalGetCredit";
import {
  ModalReturnCredit,
  type ModalReturnCreditProps,
} from "../ModalReturnCredit";
import TableCreditAccounts from "./TableCreditAccounts";
import TableInvoices from "./TableInvoices";
import TableProformaInvoices, {
  type TableProformaInvoicesData,
} from "./TableProformaInvoices";

type ModalInvoicesProps = {
  onTransfer?: () => void;
  ourWorkBudgetItemId?: string;
  expenseBudgetIdemId?: string;
  projectId?: string;
};

function transformProformaTableData(
  data: NonNullable<
    ModalInvoicesOurWorkBiQuery["ourWorkBudgetItem"]
  >["outgoingProformaInvoiceStats"],
): TableProformaInvoicesData[] {
  return data.map((stat) => ({
    id: stat.id,
    invoice_no: stat.invoice_no,
    issue_date: stat.date,
    stats: {
      total_amount: stat.amount,
    },
    documentExports: [],
    emailLogs: [],
  }));
}

const ModalInvoices: FC<
  React.PropsWithChildren<ModalInvoicesProps & UseModalProps>
> = ({
  ourWorkBudgetItemId,
  expenseBudgetIdemId,
  projectId,
  onTransfer,
  ...modalProps
}) => {
  const { loading: loadingProject, data: dataProject } =
    useModalInvoicesProjectQuery({
      variables: { id: projectId! },
      skip: !projectId || !modalProps.isOpen,
      ...onError(),
    });

  const { loading: loadingOurWorkBI, data: dataOurWorkBI } =
    useModalInvoicesOurWorkBiQuery({
      variables: { id: ourWorkBudgetItemId! },
      skip: !ourWorkBudgetItemId || !modalProps.isOpen,
      ...onError(),
    });
  const owbiProformaTableData = transformProformaTableData(
    dataOurWorkBI?.ourWorkBudgetItem?.outgoingProformaInvoiceStats ?? [],
  );

  const { loading: loadingExpenseBI, data: dataExpenseBI } =
    useModalInvoicesExpenseBiQuery({
      variables: { id: expenseBudgetIdemId! },
      skip: !expenseBudgetIdemId || !modalProps.isOpen,
      ...onError(),
    });
  const expensebiProformaTableData = transformProformaTableData(
    dataExpenseBI?.expenseBudgetItem?.outgoingProformaInvoiceStats ?? [],
  );

  const title = useMemo(() => {
    if (dataProject?.project) {
      const invoices =
        dataProject.project.invoiceStats.length +
        dataProject.project.prepaymentStats.length;
      return (
        <React.Fragment>
          <Trans>Invoiced</Trans>{" "}
          <Plural value={invoices} one="(# invoice)" other="(# invoices)" />
        </React.Fragment>
      );
    }

    if (dataOurWorkBI?.ourWorkBudgetItem) {
      return t`Budget item invoicing (${dataOurWorkBI.ourWorkBudgetItem.title})`;
    }

    if (dataExpenseBI?.expenseBudgetItem) {
      return t`Budget item invoicing (${dataExpenseBI.expenseBudgetItem.title})`;
    }

    return "";
  }, [dataProject, dataOurWorkBI, dataExpenseBI]);

  const router = useRouter();

  const getCreditModal = useDisclosure();
  const returnCreditModal = useDisclosure();

  const [returnCreditProps, setReturnCreditProps] =
    useState<ModalReturnCreditProps | null>(null);

  const onOpenReturnCredit = (props: ModalReturnCreditProps) => {
    setReturnCreditProps(props);
    returnCreditModal.onOpen();
  };

  const onBudgetItem = (hash: string) => {
    modalProps.onClose();
    router.push({
      pathname: "/projects/detail",
      query: { id: projectId },
      hash,
    });
  };

  return (
    <React.Fragment>
      <Modal
        isOpen={
          modalProps.isOpen &&
          !getCreditModal.isOpen &&
          !returnCreditModal.isOpen
        }
        onClose={modalProps.onClose}
        size={ourWorkBudgetItemId || expenseBudgetIdemId ? "5xl" : "4xl"}
      >
        <ModalOverlay />
        <ModalContent>
          {(loadingProject || loadingOurWorkBI || loadingExpenseBI) && (
            <Center
              pos="absolute"
              zIndex={100}
              top="0"
              right="0"
              bottom="0"
              left="0"
              bg="white"
              rounded="3xl"
            >
              <Spinner color="teal.500" size="lg" />
            </Center>
          )}
          <ModalHeader>{title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0}>
            <TableInvoices
              isProjectBillable={
                dataProject?.project?.billable ||
                dataOurWorkBI?.ourWorkBudgetItem?.project.billable ||
                dataExpenseBI?.expenseBudgetItem?.project.billable
              }
              data={
                dataProject?.project?.invoiceStats ||
                dataOurWorkBI?.ourWorkBudgetItem?.invoiceStats ||
                dataExpenseBI?.expenseBudgetItem?.invoiceStats ||
                []
              }
              invoicedAmount={
                dataProject?.project?.statsCached?.credit_direct ??
                dataOurWorkBI?.ourWorkBudgetItem?.stats.credit_direct ??
                dataExpenseBI?.expenseBudgetItem?.stats.credit_direct ??
                0
              }
              toInvoiceAmount={
                dataOurWorkBI?.ourWorkBudgetItem?.stats.uninvoiced ??
                dataExpenseBI?.expenseBudgetItem?.stats.uninvoiced ??
                undefined
              }
              projectId={
                (
                  dataOurWorkBI?.ourWorkBudgetItem ||
                  dataExpenseBI?.expenseBudgetItem
                )?.project.id ?? projectId
              }
              ourWorkBudgetItemId={ourWorkBudgetItemId}
              expenseBudgetIdemId={expenseBudgetIdemId}
            />
            {dataProject?.project?.proformaInvoices && (
              <TableProformaInvoices
                data={dataProject.project.proformaInvoices}
                unusedAmount={
                  dataProject.project.statsCached?.unused_sum_from_proformas ??
                  0
                }
              />
            )}
            {dataOurWorkBI?.ourWorkBudgetItem?.outgoingProformaInvoiceStats && (
              <TableProformaInvoices
                unusedAmount={0}
                data={owbiProformaTableData}
              />
            )}
            {dataExpenseBI?.expenseBudgetItem?.outgoingProformaInvoiceStats && (
              <TableProformaInvoices
                unusedAmount={0}
                data={expensebiProformaTableData}
              />
            )}
            <TableCreditAccounts
              data={
                dataProject?.project?.prepaymentStats ||
                dataOurWorkBI?.ourWorkBudgetItem?.prepaymentStats ||
                dataExpenseBI?.expenseBudgetItem?.prepaymentStats ||
                []
              }
              usedAmount={
                dataProject?.project?.statsCached?.credit_from_prepayments ??
                dataOurWorkBI?.ourWorkBudgetItem?.stats
                  .credit_from_prepayments ??
                dataExpenseBI?.expenseBudgetItem?.stats
                  .credit_from_prepayments ??
                0
              }
              onBudgetItem={projectId ? onBudgetItem : undefined}
              onOpenGetCredit={projectId ? undefined : getCreditModal.onOpen}
              onOpenReturnCredit={projectId ? undefined : onOpenReturnCredit}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {returnCreditProps && (
        <ModalReturnCredit
          {...returnCreditModal}
          {...returnCreditProps}
          onSubmit={onTransfer}
        />
      )}

      {(dataExpenseBI?.expenseBudgetItem ||
        dataOurWorkBI?.ourWorkBudgetItem) && (
        <ModalGetCredit
          {...getCreditModal}
          brandId={
            (
              dataOurWorkBI?.ourWorkBudgetItem ||
              dataExpenseBI?.expenseBudgetItem
            )?.project.brand.id!
          }
          creditAccountId={
            (
              dataOurWorkBI?.ourWorkBudgetItem ||
              dataExpenseBI?.expenseBudgetItem
            )?.creditAccount?.id!
          }
          credit={
            (
              dataOurWorkBI?.ourWorkBudgetItem ||
              dataExpenseBI?.expenseBudgetItem
            )?.stats.credit!
          }
          onSubmit={onTransfer}
        />
      )}
    </React.Fragment>
  );
};

export default observer(ModalInvoices);
